/**
 * Deterministic client-side fuzzy matching for the launcher (no network, no AI).
 * Every whitespace-separated query token must match the target as a subsequence; the score rewards
 * prefixes, word starts and consecutive runs, and penalises gaps.
 */

const BOUNDARY = /[\s\-_./:·@#>]/;

function tokenScore(token: string, target: string): number | null {
  if (!token) return 0;
  const exact = target.indexOf(token);
  if (exact !== -1) {
    const atWord = exact === 0 || BOUNDARY.test(target[exact - 1]!);
    return 100 + token.length * 4 + (exact === 0 ? 40 : atWord ? 20 : 0) - Math.min(exact, 30);
  }
  let score = 0;
  let ti = 0;
  let prev = -2;
  for (const ch of token) {
    let found = -1;
    for (let i = ti; i < target.length; i++) {
      if (target[i] === ch) {
        found = i;
        break;
      }
    }
    if (found === -1) return null;
    const atWord = found === 0 || BOUNDARY.test(target[found - 1]!);
    score += 1;
    if (found === prev + 1) score += 5;
    if (atWord) score += 8;
    if (found === 0) score += 6;
    score -= Math.min(found - ti, 8) * 0.5;
    prev = found;
    ti = found + 1;
  }
  return score;
}

/** Score `query` against `target` (higher is better); null when it does not match. */
export function fuzzyScore(query: string, target: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const t = target.toLowerCase();
  // Exact matches win; shorter targets win ties ("Rex" over "Rex · task running").
  let total = (t === q ? 50 : 0) - Math.min(t.length, 80) * 0.05;
  for (const token of q.split(/\s+/)) {
    const s = tokenScore(token, t);
    if (s == null) return null;
    total += s;
  }
  return total;
}

/** Rank items by their best-matching field. Ties keep the input order (stable). */
export function fuzzyRank<T>(items: T[], query: string, fields: (item: T) => (string | undefined)[]): T[] {
  if (!query.trim()) return items;
  const scored: { item: T; score: number; i: number }[] = [];
  items.forEach((item, i) => {
    let best: number | null = null;
    fields(item).forEach((f, idx) => {
      if (!f) return;
      const s = fuzzyScore(query, f);
      // The first field (title) wins over secondary keywords.
      if (s != null) best = Math.max(best ?? -Infinity, idx === 0 ? s : s * 0.8);
    });
    if (best != null) scored.push({ item, score: best, i });
  });
  return scored.sort((a, b) => b.score - a.score || a.i - b.i).map((x) => x.item);
}
